import React, { useEffect, useState } from 'react'

import {
  ChevronRightIcon,
  CloseIcon,
  DoneIcon
} from '@toasttab/buffet-pui-icons'
import classnames from 'classnames'
import { motion } from 'framer-motion'

import {
  PaginatedMenusSortCriteria,
  PaginatedMenusSortField,
  PaginatedMenusSortOrder
} from 'src/apollo/onlineOrdering'
import DropDown from 'src/shared/components/common/dropdown'
import { DropDownOption } from 'src/shared/components/common/dropdown/DropDownOption'
import { t } from 'src/translations'
import { ScreenWidth, useIsMobile } from 'src/shared/js/utils/WindowContext'
import {
  Modal,
  ModalContent,
  ModalOverlay
} from 'shared/components/common/modal'
import { BrandedButton } from 'shared/components/common/button'
import { ButtonType } from 'src/apollo/sites'

type SortOption = {
  labelKey: string
  value: PaginatedMenusSortCriteria[] | null
}

const SORT_OPTIONS: SortOption[] = [
  {
    labelKey: 'sort.default',
    value: null
  },
  {
    labelKey: 'sort.price-low-high',
    value: [
      {
        field: PaginatedMenusSortField.Price,
        order: PaginatedMenusSortOrder.Asc
      }
    ]
  },
  {
    labelKey: 'sort.price-high-low',
    value: [
      {
        field: PaginatedMenusSortField.Price,
        order: PaginatedMenusSortOrder.Desc
      }
    ]
  },
  {
    labelKey: 'sort.name-a-z',
    value: [
      {
        field: PaginatedMenusSortField.Name,
        order: PaginatedMenusSortOrder.Asc
      }
    ]
  },
  {
    labelKey: 'sort.name-z-a',
    value: [
      {
        field: PaginatedMenusSortField.Name,
        order: PaginatedMenusSortOrder.Desc
      }
    ]
  }
]

type SortProps = {
  onSortChange: (sort: PaginatedMenusSortCriteria[] | null) => void
  currentSort: PaginatedMenusSortCriteria[] | undefined | null
}

type SortButtonProps = {
  onClick: (e: React.MouseEvent) => void
  currentSort: PaginatedMenusSortCriteria[] | undefined | null
  disabled?: boolean
  expanded?: boolean
}

export const SortDropdown = ({ onSortChange, currentSort }: SortProps) => {
  return (
    <DropDown
      label=''
      opener={(toggle, expanded) => (
        <SortButton
          onClick={toggle}
          currentSort={currentSort}
          expanded={expanded}
        />
      )}
      ariaLabel={t('sort.title')}
      className='sortDropdown'
    >
      {({ close }) => (
        <>
          {SORT_OPTIONS.map((option, index) => (
            <DropDownOption
              key={index}
              onSelect={() => {
                // Skip calling onSortChange when re-selecting the current sort so it doesn't fire another MENU_SORT_CHANGED event
                if (
                  JSON.stringify(option.value) !==
                  JSON.stringify(currentSort ?? null)
                ) {
                  onSortChange(option.value)
                }
                close()
              }}
              onClose={close}
              className='option'
              testId={`sort-option-${index}`}
            >
              {t(option.labelKey)}
            </DropDownOption>
          ))}
        </>
      )}
    </DropDown>
  )
}

type SortModalProps = {
  isOpen: boolean
  onClose: () => void
  onSortChange: (sort: PaginatedMenusSortCriteria[] | null) => void
  currentSort: PaginatedMenusSortCriteria[] | undefined | null
}

export const SortModal = ({
  isOpen,
  onClose,
  onSortChange,
  currentSort
}: SortModalProps) => {
  const [tempSort, setTempSort] = useState<PaginatedMenusSortCriteria[] | null>(
    currentSort ?? null
  )

  useEffect(() => {
    if (isOpen) {
      setTempSort(currentSort ?? null)
    }
  }, [isOpen, currentSort])

  const handleApply = () => {
    if (!isSelected(currentSort ?? null)) {
      onSortChange(tempSort)
    }
    onClose()
  }

  const isSelected = (value: PaginatedMenusSortCriteria[] | null) => {
    return JSON.stringify(value) === JSON.stringify(tempSort)
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} animateClose={false}>
      <ModalOverlay fadeIn fadeOut />
      <ModalContent wrapperClassName='sortModalWrapper'>
        <motion.div
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{
            duration: 0.2,
            ease: 'easeIn'
          }}
          className='sortModalContent'
        >
          <div className='sortModalHeader'>
            <div className='sortModalTitle'>{t('sort.title')}</div>
            <button
              onClick={onClose}
              className='sortModalCloseButton'
              aria-label='Close'
            >
              <CloseIcon />
            </button>
          </div>

          <div className='sortModalOptions'>
            {SORT_OPTIONS.map((option, index) => (
              <button
                key={index}
                onClick={() => setTempSort(option.value)}
                className='sortModalOption'
                data-testid={`sort-option-${index}`}
                aria-pressed={isSelected(option.value)}
              >
                <span
                  className={classnames('sortModalOptionText', {
                    selected: isSelected(option.value)
                  })}
                >
                  {t(option.labelKey)}
                </span>
                {isSelected(option.value) && (
                  <DoneIcon className='sortModalCheckIcon' />
                )}
              </button>
            ))}
          </div>

          <div className='sortModalFooter'>
            <button onClick={handleApply} className='sortModalApplyButton'>
              {t('sort.apply')}
            </button>
          </div>
        </motion.div>
      </ModalContent>
    </Modal>
  )
}

export const SortButton = ({
  onClick,
  currentSort,
  disabled,
  expanded
}: SortButtonProps) => {
  const isMobile = useIsMobile(ScreenWidth.SMALL)
  const currentOption =
    SORT_OPTIONS.find(
      (option) => JSON.stringify(option.value) === JSON.stringify(currentSort)
    ) || SORT_OPTIONS[0]

  return (
    <BrandedButton
      onClick={onClick}
      variant={isMobile ? ButtonType.Text : ButtonType.Secondary}
      iconRight={<ChevronRightIcon />}
      testId='sort-dropdown'
      ariaLabel={t('sort.title')}
      className={classnames('dropdownButton', { expanded })}
      disabled={disabled}
    >
      <span className='dropdownButtonLabel' data-testid='sort-dropdown-label'>
        {t('sort.label', { option: t(currentOption?.labelKey) })}
      </span>
    </BrandedButton>
  )
}
